// Shared fetch helpers used by every lib/api/*.ts module. Post-Supabase,
// auth rides on the HttpOnly `__Host-session` cookie (same-origin → sent
// automatically). In dev mode we attach the X-Dev-User-Id header so the
// Pages Functions middleware short-circuits its cookie check.
//
// `apiBase()` lets the build override the same-origin default via
// VITE_API_BASE_URL (used by local-stack dev where vite serves on :5174 and
// wrangler pages dev hosts the Functions on a different port).

import { DEV_USER_ID_HEADER, devUserId, isDevAuthMode } from "../devAuth";
import { ApiClientError } from "./_errors";

const apiBase = (): string => {
	const explicit = import.meta.env.VITE_API_BASE_URL;
	if (explicit && explicit.length > 0) return explicit.replace(/\/+$/, "");
	return "";
};

function buildHeaders(init?: RequestInit): Headers {
	const headers = new Headers(init?.headers);
	if (!headers.has("Content-Type")) {
		headers.set("Content-Type", "application/json");
	}
	if (isDevAuthMode()) {
		headers.set(DEV_USER_ID_HEADER, devUserId());
	}
	return headers;
}

export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
	return fetch(`${apiBase()}${path}`, {
		...init,
		headers: buildHeaders(init),
		credentials: "include",
	});
}

export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
	const res = await apiFetch(path, init);

	if (!res.ok) {
		let code = `http_${res.status}`;
		let message = res.statusText;
		try {
			const body = (await res.json()) as {
				error?: { code?: string; message?: string };
			};
			code = body.error?.code ?? code;
			message = body.error?.message ?? message;
		} catch {
			// Keep the http_<status> fallback.
		}
		throw new ApiClientError(code, message, res.status);
	}

	if (res.status === 204) return undefined as T;
	return (await res.json()) as T;
}
